/**
 * Horse Assignment Cell Component
 * Displays horse assignments for a player in the signup table
 * Allows managers to open the assignment modal
 * 
 * NOTE: Assignments are now passed from parent (loaded with roster data)
 * to avoid per-player API calls. Updates are handled via updatePlayerAssignments callback.
 */
import { useState } from "react";
import {
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { HorseIcon, HorseAssignmentModal, HorseAssignment } from "../HorseManagement";

interface HorseAssignmentWithColor extends HorseAssignment {
  horse_color?: string;
}

interface HorseAssignmentCellProps {
  devPort: string;
  clubId: number;
  eventType: "games" | "lessons" | "tournaments";
  eventId: string | number;
  playerId: string | number;
  playerName: string;
  chukkersRequested?: number;
  isManager: boolean;
  // Assignments are now passed from parent (loaded with roster)
  assignments?: HorseAssignmentWithColor[];
  // Callback to update assignments in parent state (no re-fetch needed)
  updatePlayerAssignments?: (playerId: string | number, assignments: HorseAssignmentWithColor[]) => void;
  managerUserId?: string | null;
}

function HorseAssignmentCell({
  devPort,
  clubId,
  eventType,
  eventId,
  playerId,
  playerName,
  chukkersRequested = 0,
  isManager,
  assignments = [],
  updatePlayerAssignments,
  managerUserId,
}: HorseAssignmentCellProps) {
  const [modalOpen, setModalOpen] = useState(false);

  // Ensure assignments have horse_color
  const enrichedAssignments: HorseAssignmentWithColor[] = assignments.map((a) => ({
    ...a,
    horse_color: a.horse_color || "Brown",
  }));

  const totalAssignedChukkers = enrichedAssignments.reduce(
    (sum, a) => sum + a.chukkers_assigned,
    0
  );

  // For lessons, the request is for 1 horse. For games/tournaments, use the request value
  const isLesson = eventType === "lessons";
  const chukkersNeeded = isLesson ? 1 : chukkersRequested;
  const isFullyAssigned = chukkersNeeded > 0 && totalAssignedChukkers >= chukkersNeeded;
  const hasAssignments = enrichedAssignments.length > 0;

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Handle assignment changes from modal - update parent state directly
  const handleAssignmentsChange = (newAssignments: HorseAssignmentWithColor[]) => {
    if (updatePlayerAssignments) {
      updatePlayerAssignments(playerId, newAssignments);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: "wrap", justifyContent: "center" }}>
        {!hasAssignments ? (
          <Typography color="text.secondary">-</Typography>
        ) : (
          <>
            {enrichedAssignments.map((assignment) => (
              <Tooltip
                key={assignment.id}
                title={`${assignment.horse_name}: ${assignment.chukkers_assigned} chukker${assignment.chukkers_assigned > 1 ? "s" : ""}`}
              >
                <Chip
                  size="small"
                  icon={<Box sx={{ ml: 0.5 }}><HorseIcon color={assignment.horse_color || "Brown"} size={16} /></Box>}
                  label={isLesson ? assignment.horse_name : `${assignment.chukkers_assigned}`}
                  variant={isFullyAssigned ? "filled" : "outlined"}
                  color={isFullyAssigned ? "success" : "warning"}
                  sx={{ m: 0.25 }}
                />
              </Tooltip>
            ))}
            {!isLesson && !isFullyAssigned && chukkersNeeded > 0 && (
              <Typography variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                ({totalAssignedChukkers}/{chukkersNeeded})
              </Typography>
            )}
          </>
        )}
        
        {isManager && (
          <Tooltip title="Manage horse assignments">
            <IconButton size="small" onClick={handleOpenModal} sx={{ ml: 0.5 }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {modalOpen && (
        <HorseAssignmentModal
          open={modalOpen}
          onClose={handleCloseModal}
          devPort={devPort}
          clubId={clubId}
          eventType={eventType}
          eventId={eventId}
          playerId={playerId}
          playerName={playerName}
          maxChukkers={chukkersNeeded || chukkersRequested || 1}
          onAssignmentsChange={handleAssignmentsChange}
          managerUserId={managerUserId}
        />
      )}
    </>
  );
}

export default HorseAssignmentCell;
